import { useState, useEffect, useCallback, useMemo } from 'react'
import { GridApi, GridFilterModel, GridApiRef } from '@mui/x-data-grid'
import { gridFilteredSortedRowIdsSelector } from '@mui/x-data-grid'

// Interface for filtered data hook
export interface UseFilteredDataOptions {
  tableId: string
  originalData: any[]
  onFilteredDataChange?: (tableId: string, filteredData: any[]) => void
}

export interface UseFilteredDataReturn {
  filteredData: any[]
  handleFilterModelChange: (model: GridFilterModel, apiRef?: GridApiRef) => void
  getFilteredRowsFromApi: (apiRef: GridApiRef) => any[]
  triggerFilteredDataUpdate: (apiRef?: GridApiRef) => void
}

export const useFilteredData = ({
  tableId,
  originalData,
  onFilteredDataChange,
}: UseFilteredDataOptions): UseFilteredDataReturn => {
  const [filteredData, setFilteredData] = useState<any[]>(originalData)
  const [currentApiRef, setCurrentApiRef] = useState<GridApiRef | null>(null)

  // Update filtered data when original data changes
  useEffect(() => {
    setFilteredData(originalData)
    
    // Notify parent component with initial data
    if (onFilteredDataChange) {
      onFilteredDataChange(tableId, originalData)
    }
  }, [originalData, onFilteredDataChange, tableId])

  // Function to get filtered rows from DataGrid API
  const getFilteredRowsFromApi = useCallback((apiRef: GridApiRef): any[] => {
    try {
      console.log(`🔍 Getting filtered data for ${tableId}`)
      
      // Check if apiRef is properly initialized
      if (!apiRef.current) {
        console.warn(`ApiRef not initialized for ${tableId}`)
        return originalData
      }
      
      // Try different approaches to get filtered data
      let filteredRowIds: any[] = []
      
      // Approach 1: Use the selector
      try {
        const selectorResult = gridFilteredSortedRowIdsSelector(apiRef)
        console.log(`🔍 Selector result for ${tableId}:`, { 
          type: typeof selectorResult, 
          isArray: Array.isArray(selectorResult),
          length: selectorResult?.length
        })
        
        if (Array.isArray(selectorResult)) {
          filteredRowIds = selectorResult
        }
      } catch (selectorError) {
        console.warn(`Selector failed for ${tableId}:`, selectorError)
      }
      
      // Approach 2: Fallback to getSortedRowIds
      if (filteredRowIds.length === 0) {
        try {
          filteredRowIds = apiRef.current.getSortedRowIds()
          console.log(`📋 Using getSortedRowIds fallback for ${tableId}:`, filteredRowIds.length)
        } catch (sortedError) {
          console.warn(`getSortedRowIds failed for ${tableId}:`, sortedError)
        }
      }
      
      // Approach 3: Manual filtering using filter model
      if (filteredRowIds.length === 0 || filteredRowIds.length === originalData.length) {
        try {
          // Check if getFilterModel is available before calling it
          if (apiRef.current && typeof apiRef.current.getFilterModel === 'function') {
            const filterModel = apiRef.current.getFilterModel()
            console.log(`🔍 Filter model for ${tableId}:`, filterModel)
          
            if (filterModel.items && filterModel.items.length > 0) {
              // Apply manual filtering
              const manuallyFiltered = originalData.filter(row => {
                return filterModel.items!.every(filterItem => {
                  if (!filterItem.field || !filterItem.operator || filterItem.value === undefined) {
                    return true
                  }
                  
                  const cellValue = row[filterItem.field]
                  const filterValue = filterItem.value
                  
                  switch (filterItem.operator) {
                    case 'contains':
                      return String(cellValue).toLowerCase().includes(String(filterValue).toLowerCase())
                    case 'equals':
                      return cellValue === filterValue
                    case 'startsWith':
                      return String(cellValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
                    case 'endsWith':
                      return String(cellValue).toLowerCase().endsWith(String(filterValue).toLowerCase())
                    case 'isEmpty':
                      return cellValue === '' || cellValue === null || cellValue === undefined
                    case 'isNotEmpty':
                      return cellValue !== '' && cellValue !== null && cellValue !== undefined
                    default:
                      return true
                  }
                })
              })
              
              console.log(`✅ Manual filtering applied for ${tableId}: ${manuallyFiltered.length} rows`)
              return manuallyFiltered
            }
          }
        } catch (filterError) {
          console.warn(`Manual filtering failed for ${tableId}:`, filterError)
        }
      }
      
      // Get the actual row data using the filtered IDs
      if (filteredRowIds.length > 0) {
        const filteredRows = filteredRowIds.map(id => {
          try {
            const row = apiRef.current.getRow(id)
            return row
          } catch (rowError) {
            console.warn(`Failed to get row ${id} for ${tableId}:`, rowError)
            return null
          }
        }).filter(Boolean)

        console.log(`✅ Successfully got ${filteredRows.length} filtered rows for ${tableId}`)
        return filteredRows
      }
      
      // Final fallback
      console.log(`📋 Using original data fallback for ${tableId}`)
      return originalData
      
    } catch (error) {
      console.warn('Failed to get filtered rows from API:', error)
      // Fallback to original data if API fails
      return originalData
    }
  }, [originalData, tableId])

  // Function to trigger filtered data update (used by global filter sync)
  const triggerFilteredDataUpdate = useCallback((apiRef?: GridApiRef) => {
    const apiToUse = apiRef || currentApiRef
    if (apiToUse) {
      console.log(`🔄 Triggering filtered data update for ${tableId}`)
      const newFilteredData = getFilteredRowsFromApi(apiToUse)
      setFilteredData(newFilteredData)
      
      // Notify parent component
      if (onFilteredDataChange) {
        onFilteredDataChange(tableId, newFilteredData)
      }
    } else {
      console.warn(`⚠️ No apiRef available for ${tableId} filtered data update`)
    }
  }, [tableId, currentApiRef, getFilteredRowsFromApi, onFilteredDataChange])

  // Handle filter model change
  const handleFilterModelChange = useCallback((model: GridFilterModel, apiRef?: GridApiRef) => {
    if (apiRef) {
      // Store the apiRef for future use
      setCurrentApiRef(apiRef)
      
      // Use a timeout to ensure DataGrid has processed the filter changes
      setTimeout(() => {
        triggerFilteredDataUpdate(apiRef)
      }, 100) // Small delay to ensure DataGrid processes the filter
    } else {
      // Fallback: apply basic filtering manually
      let filtered = [...originalData]
      
      if (model.items && model.items.length > 0) {
        filtered = originalData.filter(row => {
          return model.items!.every(filterItem => {
            if (!filterItem.field || !filterItem.operator || filterItem.value === undefined) {
              return true
            }

            const cellValue = row[filterItem.field]
            const filterValue = filterItem.value

            switch (filterItem.operator) {
              case 'contains':
                return String(cellValue).toLowerCase().includes(String(filterValue).toLowerCase())
              case 'equals':
                return cellValue === filterValue
              case 'startsWith':
                return String(cellValue).toLowerCase().startsWith(String(filterValue).toLowerCase())
              case 'endsWith':
                return String(cellValue).toLowerCase().endsWith(String(filterValue).toLowerCase())
              case 'isEmpty':
                return cellValue === '' || cellValue === null || cellValue === undefined
              case 'isNotEmpty':
                return cellValue !== '' && cellValue !== null && cellValue !== undefined
              case '>':
                return Number(cellValue) > Number(filterValue)
              case '>=':
                return Number(cellValue) >= Number(filterValue)
              case '<':
                return Number(cellValue) < Number(filterValue)
              case '<=':
                return Number(cellValue) <= Number(filterValue)
              default:
                return true
            }
          })
        })
      }

      // Apply quick filter if present
      if (model.quickFilterValues && model.quickFilterValues.length > 0) {
        const quickFilterValue = model.quickFilterValues[0].toLowerCase()
        filtered = filtered.filter(row => {
          return Object.values(row).some(value => 
            String(value).toLowerCase().includes(quickFilterValue)
          )
        })
      }

      setFilteredData(filtered)
      
      // Notify parent component
      if (onFilteredDataChange) {
        onFilteredDataChange(tableId, filtered)
      }
    }
  }, [tableId, originalData, onFilteredDataChange, triggerFilteredDataUpdate])

  return useMemo(() => ({
    filteredData,
    handleFilterModelChange,
    getFilteredRowsFromApi,
    triggerFilteredDataUpdate,
  }), [filteredData, handleFilterModelChange, getFilteredRowsFromApi, triggerFilteredDataUpdate])
}

export default useFilteredData